import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Camera, Smartphone, RefreshCw, CheckCircle, AlertCircle,
  X, HelpCircle, Users, UserX, ChevronDown, QrCode, Send, Loader2, XCircle
} from 'lucide-react';
import { usePhotoWebSocket } from '../../hooks/usePhotoWebSocket';
import { useSharedWebSocket } from '../../../hooks/useSharedWebSocket';

interface RemoteCameraMultiPhotoProps {
  turbineId: string;
  damageTypeId: number;
  userId: string;
  onPhotosUpdate?: (photos: Photo[]) => void;
  onRequestCompleted?: () => void;  // ✅ Chiamato quando la richiesta è completata (tutte le foto ricevute)
}

interface Photo {
  id: string;
  url: string;
  order: number;
  comment?: string;
  uploadedAt: string;
}

interface Session {
  sessionId: string;
  sessionToken: string;
  expiresAt: string;
  isActive: boolean;
}

interface MobileOperator {
  userId: string;
  userName: string;
  status: string;
  deviceInfo?: string;
  appVersion?: string;
}

type ViewMode = 'loading' | 'select-operator' | 'no-operators' | 'qr-code' | 'waiting-response' | 'accepted' | 'refused';

interface AcceptedBy {
  userId: string;
  userName: string;
}

const RemoteCameraMultiPhoto: React.FC<RemoteCameraMultiPhotoProps> = ({
  turbineId,
  damageTypeId,
  userId,
  onPhotosUpdate,
  onRequestCompleted
}) => {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('loading');

  // Mobile operators
  const [availableOperators, setAvailableOperators] = useState<MobileOperator[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<MobileOperator | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // QR Code session (solo se richiesto)
  const [session, setSession] = useState<Session | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [countdown, setCountdown] = useState<string>('');

  // Request state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [_requestSent, setRequestSent] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [acceptedBy, setAcceptedBy] = useState<AcceptedBy | null>(null);
  const [_currentRequestId, _setCurrentRequestId] = useState<string | null>(null);
  const [requestedPhotoCount, setRequestedPhotoCount] = useState<number>(1); // Numero foto richieste

  // WebSocket condiviso per ricevere notifiche di accettazione/rifiuto
  const { registerHandler } = useSharedWebSocket({
    userId,
    userName: 'Desktop User',
    userType: 'desktop',
    enabled: true
  });

  // Registra handlers per eventi photo request
  useEffect(() => {
    // Handler per richiesta accettata (sistema DM)
    const unregisterAccepted = registerHandler('photo_request_accepted', (data: any) => {
      console.log('[RemoteCamera] ✅ Richiesta foto ACCETTATA:', data);
      if (viewMode === 'waiting-response') {
        setAcceptedBy(data.acceptedBy || { userId: 'unknown', userName: 'Operatore' });
        setViewMode('accepted');
      }
    });

    // Handler per richiesta rifiutata (sistema DM)
    const unregisterRefused = registerHandler('photo_request_refused', (data: any) => {
      console.log('[RemoteCamera] ❌ Richiesta foto RIFIUTATA:', data);
      if (viewMode === 'waiting-response') {
        setViewMode('refused');
      }
    });

    // Handler per richiesta assegnata (sistema foto)
    const unregisterAssigned = registerHandler('request_assigned', (data: any) => {
      console.log('[RemoteCamera] ✅ Richiesta assegnata:', data);
      if (viewMode === 'waiting-response') {
        setAcceptedBy(data.assignedTo || { userId: 'unknown', userName: 'Operatore' });
        setViewMode('accepted');
      }
    });

    // Handler per foto caricata dal mobile (HTTP upload)
    const unregisterPhotoUploaded = registerHandler('photo_uploaded', (data: any) => {
      console.log('[RemoteCamera] 📸 photo_uploaded ricevuto:', data);
      if (data.url && onPhotosUpdate) {
        // Crea oggetto foto e notifica parent
        const newPhoto = {
          id: data.photoId || `photo-${Date.now()}`,
          url: data.url.startsWith('/') ? data.url : `/${data.url}`,
          order: 1,
          uploadedAt: data.timestamp || new Date().toISOString()
        };
        console.log('[RemoteCamera] 📸 Notifica parent con nuova foto:', newPhoto);
        onPhotosUpdate([newPhoto]);
      }
    });

    // Handler per richiesta completata dal mobile
    const unregisterRequestCompleted = registerHandler('request_completed_by_mobile', (data: any) => {
      console.log('[RemoteCamera] ✅ request_completed_by_mobile ricevuto:', data);
      if (data.photos && data.photos.length > 0 && onPhotosUpdate) {
        const photos = data.photos.map((p: any, idx: number) => ({
          id: p.photoId || `photo-${Date.now()}-${idx}`,
          url: (p.url || p).startsWith('/') ? (p.url || p) : `/${p.url || p}`,
          order: idx + 1,
          uploadedAt: p.timestamp || new Date().toISOString()
        }));
        console.log('[RemoteCamera] ✅ Notifica parent con foto completate:', photos);
        onPhotosUpdate(photos);
      }
      // ✅ Chiama onRequestCompleted per chiudere il modal
      onRequestCompleted?.();
    });

    return () => {
      unregisterAccepted();
      unregisterRefused();
      unregisterAssigned();
      unregisterPhotoUploaded();
      unregisterRequestCompleted();
    };
  }, [registerHandler, viewMode, onPhotosUpdate, onRequestCompleted]);

  // WebSocket hook per foto real-time
  const { photos, wsConnected, usePolling, refreshPhotos } = usePhotoWebSocket({
    turbineId,
    damageTypeId,
    sessionToken: session?.sessionToken || '',
    enabled: !!session && session.isActive,
    onPhotoUploaded: (photo) => {
      console.log('[RemoteCamera] 📸 Nuova foto ricevuta via WebSocket:', photo);
    }
  });

  // Notifica parent quando foto cambiano
  useEffect(() => {
    if (photos.length > 0) {
      onPhotosUpdate?.(photos);
    }
  }, [photos, onPhotosUpdate]);

  // Check operatori disponibili all'avvio
  const checkAvailableOperators = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/photo-requests/mobile/available', {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Errore verifica operatori');

      const data = await response.json();

      if (data.success && data.mobiles && data.mobiles.length > 0) {
        setAvailableOperators(data.mobiles);
        // Pre-seleziona il primo operatore
        setSelectedOperator(data.mobiles[0]);
        setViewMode('select-operator');
      } else {
        setAvailableOperators([]);
        setViewMode('no-operators');
      }
    } catch (err) {
      console.error('[RemoteCamera] Errore check operatori:', err);
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      setViewMode('no-operators');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check operatori all'avvio
  useEffect(() => {
    checkAvailableOperators();
  }, [checkAvailableOperators]);

  // Invia richiesta foto all'operatore selezionato
  const sendPhotoRequest = async () => {
    if (!selectedOperator) return;

    setLoading(true);
    setError('');
    setRequestSent(false);

    try {
      const response = await fetch('/api/photo-requests/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-name': 'Desktop User'
        },
        credentials: 'include',
        body: JSON.stringify({
          title: `Foto per turbina`,
          description: `Richiesta ${requestedPhotoCount} foto per analisi componente danneggiato`,
          entityType: 'turbine',
          entityId: turbineId,
          targetUserId: selectedOperator.userId, // Richiesta targeted
          photoCount: requestedPhotoCount // Numero foto richieste
        })
      });

      if (!response.ok) throw new Error('Errore invio richiesta');

      const data = await response.json();

      if (data.success) {
        setRequestSent(true);
        setViewMode('waiting-response');
        console.log('[RemoteCamera] ✅ Richiesta inviata a', selectedOperator.userName);
      } else {
        throw new Error(data.error || 'Errore invio richiesta');
      }
    } catch (err) {
      console.error('[RemoteCamera] Errore invio richiesta:', err);
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  // Crea sessione QR code (metodo alternativo)
  const createQRSession = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${window.location.origin}/api/persistent-sessions/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          deviceInfo: {
            platform: navigator.platform,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) throw new Error('Errore creazione sessione');

      const data = await response.json();
      // L'API restituisce sessionId direttamente (non wrappato in success)
      if (!data.sessionId) throw new Error(data.error || 'Sessione non creata');

      // Calcola expiresAt da expiresIn (secondi)
      const expiresAtDate = new Date(Date.now() + (data.expiresIn || 86400) * 1000);
      const expiresAt = expiresAtDate.toISOString();

      const newSession: Session = {
        sessionId: data.sessionId,
        sessionToken: data.sessionToken || '', // Token viene ottenuto dal mobile quando scansiona
        expiresAt: expiresAt,
        isActive: true
      };

      setSession(newSession);
      setQrCodeUrl(data.qrCodeDataUrl);
      setViewMode('qr-code');
      startCountdown(expiresAt);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = (expiresAt: string) => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setCountdown('Scaduta');
        setSession(prev => prev ? { ...prev, isActive: false } : null);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  };

  const deletePhoto = async (photoId: string) => {
    if (!session) return;

    try {
      const response = await fetch(
        `${window.location.origin}/api/photo-sessions/${session.sessionId}/photos/${photoId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${session.sessionToken}` }
        }
      );

      if (response.ok) {
        refreshPhotos();
      }
    } catch (err) {
      console.error('Errore eliminazione foto:', err);
    }
  };

  const maxPhotos = 5;
  const photosRemaining = maxPhotos - photos.length;

  // Render contenuto in base alla view mode
  const renderContent = () => {
    switch (viewMode) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-green-400 animate-spin mb-4" />
            <p className="text-gray-400">Verifica operatori disponibili...</p>
          </div>
        );

      case 'select-operator':
        return (
          <div className="space-y-6">
            {/* Titolo sezione */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-200">Operatori Mobile Disponibili</h4>
                <p className="text-sm text-gray-400">{availableOperators.length} operatore/i connesso/i</p>
              </div>
            </div>

            {/* Dropdown selezione operatore */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-gray-200">
                    {selectedOperator?.userName || 'Seleziona operatore'}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-gray-700 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                  {availableOperators.map((op) => (
                    <button
                      key={op.userId}
                      onClick={() => {
                        setSelectedOperator(op);
                        setDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-600 transition-colors ${
                        selectedOperator?.userId === op.userId ? 'bg-green-600/20' : ''
                      }`}
                    >
                      <div className="w-3 h-3 bg-green-500 rounded-full" />
                      <div className="text-left">
                        <p className="text-gray-200 font-medium">{op.userName}</p>
                        <p className="text-xs text-gray-400">
                          {op.deviceInfo || 'Mobile'}
                          {op.appVersion && ` • v${op.appVersion}`}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Numero foto richieste */}
            <div className="flex items-center gap-3 mb-4">
              <label className="text-gray-300 text-sm whitespace-nowrap">Numero foto:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRequestedPhotoCount(Math.max(1, requestedPhotoCount - 1))}
                  className="w-8 h-8 flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  -
                </button>
                <span className="w-8 text-center text-white font-semibold">{requestedPhotoCount}</span>
                <button
                  onClick={() => setRequestedPhotoCount(Math.min(10, requestedPhotoCount + 1))}
                  className="w-8 h-8 flex items-center justify-center bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  +
                </button>
              </div>
              <span className="text-gray-500 text-xs">(max 10)</span>
            </div>

            {/* Pulsante richiedi foto */}
            <button
              onClick={sendPhotoRequest}
              disabled={loading || !selectedOperator}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-xl font-semibold transition-all shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Invio richiesta...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Richiedi {requestedPhotoCount} Foto a {selectedOperator?.userName}
                </>
              )}
            </button>

            {/* Opzione QR code alternativa */}
            <div className="border-t border-gray-700 pt-4">
              <button
                onClick={createQRSession}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-400 hover:text-gray-200 transition-colors text-sm"
              >
                <QrCode className="w-4 h-4" />
                Oppure usa QR Code manuale
              </button>
            </div>
          </div>
        );

      case 'no-operators':
        return (
          <div className="text-center py-8 space-y-6">
            <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto">
              <UserX className="w-10 h-10 text-yellow-500" />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-200 mb-2">
                Nessun Operatore Mobile Connesso
              </h4>
              <p className="text-gray-400 text-sm max-w-md mx-auto">
                Al momento non ci sono operatori mobile disponibili.
                Puoi generare un QR code da far scansionare a un operatore
                oppure attendere che un operatore si connetta.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={createQRSession}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <QrCode className="w-5 h-5" />
                    Genera QR Code
                  </>
                )}
              </button>

              <button
                onClick={checkAvailableOperators}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-gray-400 hover:text-gray-200 transition-colors text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Ricontrolla operatori disponibili
              </button>
            </div>
          </div>
        );

      case 'waiting-response':
        return (
          <div className="text-center py-8 space-y-6">
            <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto">
              <Smartphone className="w-10 h-10 text-blue-400 animate-pulse" />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-200 mb-2">
                Richiesta Inviata
              </h4>
              <p className="text-gray-400 text-sm">
                In attesa di risposta da <span className="text-green-400 font-medium">{selectedOperator?.userName}</span>
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              In attesa che l'operatore accetti la richiesta...
            </div>

            <button
              onClick={() => {
                setRequestSent(false);
                setViewMode('select-operator');
              }}
              className="text-gray-400 hover:text-gray-200 text-sm underline"
            >
              Annulla e scegli altro operatore
            </button>
          </div>
        );

      case 'accepted':
        return (
          <div className="text-center py-8 space-y-6">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-200 mb-2">
                Richiesta Accettata!
              </h4>
              <p className="text-gray-400 text-sm">
                <span className="text-green-400 font-medium">{acceptedBy?.userName || selectedOperator?.userName}</span> ha accettato la richiesta
              </p>
            </div>

            <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
              <p className="text-sm text-green-300">
                L'operatore sta scattando le foto. Le immagini appariranno automaticamente qui quando saranno caricate.
              </p>
            </div>

            <button
              onClick={() => {
                setAcceptedBy(null);
                setViewMode('select-operator');
                checkAvailableOperators();
              }}
              className="text-gray-400 hover:text-gray-200 text-sm underline"
            >
              Invia nuova richiesta
            </button>
          </div>
        );

      case 'refused':
        return (
          <div className="text-center py-8 space-y-6">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-200 mb-2">
                Richiesta Rifiutata
              </h4>
              <p className="text-gray-400 text-sm">
                L'operatore ha rifiutato la richiesta. Puoi provare con un altro operatore.
              </p>
            </div>

            <button
              onClick={() => {
                setViewMode('select-operator');
                checkAvailableOperators();
              }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Riprova con altro operatore
            </button>
          </div>
        );

      case 'qr-code':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg border-2 border-green-500 shadow-lg">
                <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
              </div>
              <p className="mt-3 text-sm text-gray-400 text-center">Scansiona per accedere</p>

              <div className="mt-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {session?.isActive ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-medium text-green-400">Sessione attiva</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Sessione scaduta</span>
                    </>
                  )}
                </div>
                {session?.isActive && (
                  <>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>Scade tra: {countdown}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                      <span className="text-xs text-gray-400">
                        {wsConnected ? 'WebSocket connesso' : usePolling ? 'Polling attivo' : 'Connessione...'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={createQRSession}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Rigenera
                </button>
                <button
                  onClick={() => {
                    setSession(null);
                    setQrCodeUrl('');
                    checkAvailableOperators();
                  }}
                  className="px-4 py-2 bg-red-900/50 hover:bg-red-900/70 text-red-300 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  <X className="w-3.5 h-3.5" />
                  Chiudi
                </button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-200">Foto Caricate</h4>
                <span className="text-sm text-gray-400">
                  {photos.length}/{maxPhotos} ({photosRemaining} disponibili)
                </span>
              </div>

              {photos.length === 0 ? (
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center">
                  <Camera className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">
                    Nessuna foto caricata.<br />
                    Usa lo smartphone per scattare.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.url}
                        alt={`Foto ${photo.order}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-700"
                      />
                      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded">
                        #{photo.order}
                      </div>
                      <button
                        onClick={() => deletePhoto(photo.id)}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Elimina foto"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-400 via-cyan-400 to-blue-500 p-6">
        <div className="flex items-center justify-between mb-4">
          <img src="/assets/Coronatek_Logo_92dpi_200x88-Bz4ITupL.webp" alt="CoronaTek" className="h-10" />
          {session && session.isActive && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-green-800" />
              <span className="font-mono text-gray-800">{countdown}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-10 h-10 bg-green-900/50 rounded-lg flex items-center justify-center">
            <Camera className="w-5 h-5 text-green-800" />
          </div>
          <div>
            <h3 className="font-semibold text-green-800">Fotocamera Remota</h3>
            <p className="text-sm text-gray-800">Max {maxPhotos} foto per componente</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {renderContent()}
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-br from-green-400 via-cyan-400 to-blue-500 p-4 flex justify-between items-center">
        <button
          onClick={() => setShowHelpModal(true)}
          className="text-green-800 hover:text-green-900 transition-colors flex items-center gap-1.5 text-sm font-medium"
        >
          <HelpCircle className="w-4 h-4" />
          Come funziona
        </button>
        <img src="/assets/Dialogicom_432x144-ByfqQ1hp.webp" alt="Dialogicom" className="h-8 opacity-70" />
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowHelpModal(false)}>
          <div className="bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-green-400 via-cyan-400 to-blue-500 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-green-800">📱 Come Funziona</h3>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-green-800 hover:text-green-900 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 text-gray-200 space-y-4">
              <div>
                <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                  Seleziona Operatore
                </h4>
                <p className="text-sm text-gray-300 ml-8">
                  Se ci sono operatori mobile connessi, seleziona quello a cui inviare la richiesta foto.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                  Invia Richiesta
                </h4>
                <p className="text-sm text-gray-300 ml-8">
                  L'operatore riceverà una notifica sul suo smartphone e potrà accettare la richiesta.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                  Ricezione Foto
                </h4>
                <p className="text-sm text-gray-300 ml-8">
                  Le foto scattate dall'operatore appariranno automaticamente in questa schermata.
                </p>
              </div>

              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-yellow-400 mb-2">⚡ Modalità QR Code</h4>
                <p className="text-sm text-gray-300">
                  Se non ci sono operatori disponibili, puoi generare un QR code da far scansionare.
                  Questa opzione è utile quando l'operatore non è ancora connesso all'app.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoteCameraMultiPhoto;
